import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "../../../ui/button";
import { Form, FormField } from "../../../ui/form";
import { Input } from "../../../ui/input";
import FormFieldItem from "src/common/form/FormFieldItem";
import { useTranslation } from "react-i18next";
import { Switch } from "src/Components/ui/switch";
import { Label } from "../../../ui/label";
import { useRef } from "react";
import { DialogClose } from "src/Components/ui/dialog";
import { toast } from "react-toastify";
import { SelectBoxForm } from "src/Components/selectbox/SelectBoxForm";
import { indexStore } from "src/store/indexStore";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../../ui/card";
import {
  Truck,
  MapPin,
  DollarSign,
  Gift,
  Tag,
  Eye,
  Settings as SettingsIcon,
  Sparkles,
  FileText,
} from "lucide-react";
import UAEDirhamIcon from "../../../utils/UAEDirhamIcon";
import { BsEyeSlash } from "react-icons/bs";
import { RichTextEditor } from "../../../ui/rich-text-editor";
import { TimePicker24Hour } from "../../../utils/TimePicker24Hour";

// Helper function to ensure 24-hour format (HH:mm)
const ensure24HourFormat = (timeValue) => {
  if (!timeValue) return timeValue;
  // If already in HH:mm format, return as is
  if (/^\d{2}:\d{2}$/.test(timeValue)) {
    return timeValue;
  }
  // If in HH:mm:ss format, remove seconds
  if (/^\d{2}:\d{2}:\d{2}$/.test(timeValue)) {
    return timeValue.substring(0, 5);
  }
  return timeValue;
};

function SettingstForm(props) {
  const { t } = useTranslation();
  const dialogCloseRef = useRef(null);
  // make validation and form fields values conditionally
  const formSchema = z.object({
    hidden_status_order: z.boolean().optional(),
    free_delivery_min_total: z.union([
      z
        .string()
        .refine((val) => !isNaN(Number(val)))
        .transform((val) => Number(val)),
      z.number(),
    ]),
    range_distance: z.union([
      z
        .string()
        .refine((val) => !isNaN(Number(val)))
        .transform((val) => Number(val)),
      z.number(),
    ]),
    price_per_range: z.union([
      z
        .string()
        .refine((val) => !isNaN(Number(val)))
        .transform((val) => Number(val)),
      z.number(),
    ]),
    bonus_ratio: z.union([
      z
        .string()
        .refine((val) => !isNaN(Number(val)))
        .transform((val) => Number(val)),
      z.number(),
    ]),
    max_points_usage_percentage: z.union([
      z
        .string()
        .refine((val) => !isNaN(Number(val)))
        .transform((val) => Number(val)),
      z.number(),
    ]),
    first_register_coupon_id: z.any().optional(),
    opening_time: z.string().optional(),
    closing_time: z.string().optional(),
    privacy_policy_ar: z.string().optional().nullable(),
    privacy_policy_en: z.string().optional().nullable(),
    terms_of_service_ar: z.string().optional().nullable(),
    terms_of_service_en: z.string().optional().nullable(),
  });

  // 1. Define your form.
  const form = useForm({
    resolver: zodResolver(formSchema),

    defaultValues: {
      ...props.datasource,
      // Ensure time values are in 24-hour format
      opening_time: props.datasource?.opening_time
        ? ensure24HourFormat(props.datasource.opening_time)
        : undefined,
      closing_time: props.datasource?.closing_time
        ? ensure24HourFormat(props.datasource.closing_time)
        : undefined,
    },
  });

  const onErorr = (errors) => {
    console.log(errors);
    toast.error(t("errorMessage.fillAll"));
  };
  // 2. Define a submit handler.
  function onSubmit(values) {
    console.log(values);
    props.onSubmit({
      ...values,
      hidden_status_order: values.hidden_status_order ? 1 : 0,
    });
    if (dialogCloseRef.current) {
      dialogCloseRef.current.click();
    }
  }
  const { coupons } = indexStore();
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, onErorr)}
        className="space-y-6"
      >
        {/* Delivery Settings Section */}
        <Card>
          <CardHeader className="bg-blue-50 dark:bg-blue-950/30 border-b">
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-600" />
              {t("gloabl.delivery_settings") || "Delivery Settings"}
            </CardTitle>
            <CardDescription>
              {t("gloabl.delivery_settings_desc") ||
                "Configure delivery options and pricing"}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <FormField
              control={form.control}
              name={"free_delivery_min_total"}
              render={({ field }) => (
                <FormFieldItem
                  required
                  form={form}
                  label={t("gloabl.free_delivery_min_total")}
                >
                  <div className="flex items-center gap-2">
                    <UAEDirhamIcon className="w-4 h-4 text-muted-foreground" />
                    <Input {...field} type="number" placeholder="0.00" />
                  </div>
                </FormFieldItem>
              )}
            />
            <FormField
              control={form.control}
              name={"range_distance"}
              render={({ field }) => (
                <FormFieldItem
                  required
                  form={form}
                  label={t("gloabl.range_distance")}
                >
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <Input {...field} type="number" placeholder="0" />
                  </div>
                </FormFieldItem>
              )}
            />
            <FormField
              control={form.control}
              name={"price_per_range"}
              render={({ field }) => (
                <FormFieldItem
                  required
                  form={form}
                  label={t("gloabl.price_per_range")}
                >
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <UAEDirhamIcon className="w-4 h-4 text-muted-foreground" />
                    <Input {...field} type="number" placeholder="0.00" />
                  </div>
                </FormFieldItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Points & Rewards Section */}
        <Card>
          <CardHeader className="bg-purple-50 dark:bg-purple-950/30 border-b">
            <CardTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-purple-600" />
              {t("gloabl.points_rewards") || "Points & Rewards"}
            </CardTitle>
            <CardDescription>
              {t("gloabl.points_rewards_desc") ||
                "Manage loyalty points and bonus system"}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <FormField
              control={form.control}
              name={"bonus_ratio"}
              render={({ field }) => (
                <FormFieldItem
                  required
                  form={form}
                  label={t("gloabl.bonus_ratio")}
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-muted-foreground" />
                    <Input {...field} type="number" placeholder="0" />
                  </div>
                </FormFieldItem>
              )}
            />
            <FormField
              control={form.control}
              name={"max_points_usage_percentage"}
              render={({ field }) => (
                <FormFieldItem
                  required
                  form={form}
                  label={t("gloabl.max_points_usage_percentage")}
                >
                  <div className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-muted-foreground" />
                    <Input {...field} type="number" placeholder="0" />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                </FormFieldItem>
              )}
            />
          </CardContent>
        </Card>

        {/* General Settings Section */}
        <Card>
          <CardHeader className="bg-gray-50 dark:bg-gray-950/30 border-b">
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-gray-600" />
              {t("gloabl.general_settings") || "General Settings"}
            </CardTitle>
            <CardDescription>
              {t("gloabl.general_settings_desc") ||
                "Basic application settings"}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <FormField
              control={form.control}
              name="hidden_status_order"
              render={({ field }) => (
                <FormFieldItem
                  form={form}
                  label={t("gloabl.hidden_status_order")}
                >
                  <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                    <div className="flex items-center gap-3">
                      {field.value ? (
                        <BsEyeSlash className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <Eye className="w-5 h-5 text-muted-foreground" />
                      )}
                      <div>
                        <Label className="text-sm font-medium cursor-pointer">
                          {t("gloabl.hidden_status_order")}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {t("gloabl.hidden_status_order_desc") ||
                            "Hide order status from customers"}
                        </p>
                      </div>
                    </div>
                    <Switch
                      dir="ltr"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </div>
                </FormFieldItem>
              )}
            />
            <FormField
              control={form.control}
              name={"first_register_coupon_id"}
              render={({ field }) => (
                <FormFieldItem
                  form={form}
                  label={t("gloabl.first_register_coupon_id")}
                >
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-muted-foreground" />
                    <SelectBoxForm
                      data={coupons}
                      {...field}
                      placeholder={t("gloabl.select_coupon")}
                      label={"code"}
                      valueKey={"id"}
                      className="flex-1"
                    />
                  </div>
                </FormFieldItem>
              )}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name={"opening_time"}
                render={() => (
                  <FormFieldItem form={form} label={t("gloabl.opening_time")}>
                    <TimePicker24Hour
                      name="opening_time"
                      control={form.control}
                      placeholder="00:00"
                    />
                  </FormFieldItem>
                )}
              />
              <FormField
                control={form.control}
                name={"closing_time"}
                render={() => (
                  <FormFieldItem form={form} label={t("gloabl.closing_time")}>
                    <TimePicker24Hour
                      name="closing_time"
                      control={form.control}
                      placeholder="00:00"
                    />
                  </FormFieldItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Policies & Legal Section */}
        <Card>
          <CardHeader className=" border-b">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-600" />
              {t("gloabl.policies_and_terms") || "Policies & Terms"}
            </CardTitle>
            <CardDescription>
              {t("gloabl.policies_and_terms_desc") ||
                "Edit privacy policy and terms of service content (HTML supported)"}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            <div className="flex flex-col gap-4">
              <FormField
                control={form.control}
                name="privacy_policy_ar"
                render={({ field }) => (
                  <FormFieldItem
                    form={form}
                    label={
                      t("gloabl.privacy_policy_ar_label") ||
                      "Privacy Policy (Arabic)"
                    }
                  >
                    <RichTextEditor
                      value={field.value || ""}
                      onChange={field.onChange}
                      placeholder={t("gloabl.privacy_policy_placeholder_ar")}
                    />
                  </FormFieldItem>
                )}
              />
              <FormField
                control={form.control}
                name="privacy_policy_en"
                render={({ field }) => (
                  <FormFieldItem
                    form={form}
                    label={
                      t("gloabl.privacy_policy_en_label") ||
                      "Privacy Policy (English)"
                    }
                  >
                    <RichTextEditor
                      value={field.value || ""}
                      onChange={field.onChange}
                      placeholder={t("gloabl.privacy_policy_placeholder_en")}
                    />
                  </FormFieldItem>
                )}
              />
            </div>
            <div className="flex flex-col gap-4">
              <FormField
                control={form.control}
                name="terms_of_service_ar"
                render={({ field }) => (
                  <FormFieldItem
                    form={form}
                    label={
                      t("gloabl.terms_of_service_ar_label") ||
                      "Terms of Service (Arabic)"
                    }
                  >
                    <RichTextEditor
                      value={field.value || ""}
                      onChange={field.onChange}
                      placeholder={t("gloabl.terms_of_service_placeholder_ar")}
                    />
                  </FormFieldItem>
                )}
              />
              <FormField
                control={form.control}
                name="terms_of_service_en"
                render={({ field }) => (
                  <FormFieldItem
                    form={form}
                    label={
                      t("gloabl.terms_of_service_en_label") ||
                      "Terms of Service (English)"
                    }
                  >
                    <RichTextEditor
                      value={field.value || ""}
                      onChange={field.onChange}
                      placeholder={t("gloabl.terms_of_service_placeholder_en")}
                    />
                  </FormFieldItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <DialogClose asChild>
            <Button type="button" variant="outline" size="lg">
              {t("gloabl.cancel")}
            </Button>
          </DialogClose>
          <DialogClose ref={dialogCloseRef} />
          <Button type="submit" size="lg">
            {t("gloabl.save")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
export default SettingstForm;
